import React from "react"
import { useNavigate } from "react-router"

import { AxiosError } from "axios"

import FullPageContentContainer from "../../../../components/FullPageContentContainer"
import { redirectToCheckout } from "../../../../lib/stripe"
import { useAppDispatch, useAppSelector } from "../../../../redux"
import { addSnackbar } from "../../../../redux/snackbars"
import {
  Box,
  Chip,
  LoadingButton,
  Paper,
  Stack,
  Toolbar,
  Typography,
  useMediaQuery,
} from "@podcastsoundboard/ps-lib/ui"
import DownloadIcon from "@podcastsoundboard/ps-lib/ui/icons/Download"
import PublicIcon from "@podcastsoundboard/ps-lib/ui/icons/Public"
import ShoppingCartIcon from "@podcastsoundboard/ps-lib/ui/icons/ShoppingCart"

const WindowsContent: React.FC = () => {
  const navigate = useNavigate()
  const currentUserProfile = useAppSelector((state) => state.currentUserProfile)
  const darkModeEnabled = useAppSelector((state) => state.darkModeEnabled)
  const [submitting, setSubmitting] = React.useState(false)
  const dispatch = useAppDispatch()

  const handleCheckoutClick = React.useCallback(async () => {
    setSubmitting(true)
    try {
      await redirectToCheckout({ sku: "windows" })
    } catch (_err) {
      const err = _err as AxiosError
      setSubmitting(false)
      console.error("Error creating checkout session", err)
      const text =
        (err.response?.data as { error?: string } | undefined)?.error ||
        "Failed to create checkout session."
      navigate("/app/account/windows-licenses")
      dispatch(addSnackbar({ text }))
    }
  }, [dispatch, navigate])

  const isSmallScreen = useMediaQuery("(max-width: 640px)")

  return (
    <Box className="hero pb-20">
      <Toolbar />

      <FullPageContentContainer minHeight="auto" maxWidth="xl">
        <Stack
          direction="row"
          gap="1rem"
          alignItems="center"
          flexWrap={isSmallScreen ? "wrap" : "nowrap"}
          justifyContent="center"
        >
          <Paper className="p-8 rounded-[20px]" elevation={4}>
            <Stack
              direction="column"
              spacing={3}
              justifyContent="center"
              alignItems="flex-start"
            >
              <Typography
                variant="body1"
                align="left"
                color="text.secondary"
                fontWeight={800}
                className="pt-4 mb-2"
              >
                Desktop App
              </Typography>
              <Typography
                component="h2"
                variant="h3"
                align="left"
                color="text.primary"
                className="mt-0 pb-4"
              >
                Podcast Soundboard for Windows
              </Typography>

              <Stack direction="row" spacing={2}>
                <LoadingButton
                  // style={{ borderRadius: "20px" }}
                  size="large"
                  color="info"
                  onClick={() => handleCheckoutClick()}
                  variant="contained"
                  loading={submitting}
                  startIcon={<ShoppingCartIcon />}
                >
                  Purchase
                </LoadingButton>
              </Stack>
            </Stack>

            <Typography
              variant="body1"
              align="left"
              color="text.primary"
              className="pb-8 mt-8"
              fontSize={"1.2rem"}
            >
              Podcast Soundboard for Windows is the perfect soundbite and track
              playing tool for those who need something offline.
            </Typography>

            <Stack
              direction="row"
              gap="0.5rem"
              flexWrap="wrap"
              className="mt-8"
            >
              <Chip
                size="medium"
                clickable
                color="info"
                onClick={() => navigate("/app/windows#downloads")}
                label="Download"
                icon={<DownloadIcon />}
              />
              <Chip
                size="medium"
                clickable
                color="primary"
                onClick={() =>
                  currentUserProfile
                    ? navigate("/app/account/subscription")
                    : navigate("/app/signup")
                }
                label="Web"
                icon={<PublicIcon />}
              />

              <Chip
                size="medium"
                clickable
                onClick={() => navigate("/app/windows/changelog")}
                label="Changelog"
              />
            </Stack>
          </Paper>

          <Box className="p-4">
            <img
              style={{
                maxHeight: "40vh",
                maxWidth: isSmallScreen ? "80vw" : "40vw",
              }}
              src={
                darkModeEnabled
                  ? "/img/devices/laptop-dark.png"
                  : "/img/devices/laptop-light.png"
              }
              alt={
                darkModeEnabled ? "Dark mode on mobile" : "Light mode on mobile"
              }
            />
          </Box>
        </Stack>
      </FullPageContentContainer>
    </Box>
  )
}

export default WindowsContent
